// @ts-nocheck
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Textarea } from "@libraryadmin/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@libraryadmin/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  BookOpen,
  AlertTriangle,
  Search,
  Plus,
  ArrowLeft,
  Save,
  Edit,
  Eye,
  Trash2,
  FileDown,
  User,
  Phone,
  BookCheck,
  FileText,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  getBookIssues,
  issueBook,
  getMembers,
  getBooks,
  updateBookIssue,
  deleteBookIssue,
} from "@libraryadmin/services/api";
import type {
  BookIssue as BookIssueType,
  Member,
  Book,
} from "@libraryadmin/data/seed";

const BookIssuePage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { issueId } = useParams();

  // Screen View Mode: "list" | "add" | "edit" | "view"
  const [viewMode, setViewMode] = useState<"list" | "add" | "edit" | "view">(
    "list"
  );
  const [selectedIssue, setSelectedIssue] = useState<BookIssueType | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Form state
  const initialForm = {
    issueNo: "",
    memberCode: "",
    memberName: "",
    memberPhone: "",
    bookName: "",
    remarks: "",
    issueDate: new Date().toISOString().split("T")[0],
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0],
  };
  const [form, setForm] = useState(initialForm);

  // Queries
  const {
    data: issued = [],
    isLoading,
    isError,
    error: issuesError,
  } = useQuery({
    queryKey: ["book-issues"],
    queryFn: getBookIssues,
  });

  const { data: members = [] } = useQuery({
    queryKey: ["members"],
    queryFn: getMembers,
  });

  const { data: books = [] } = useQuery({
    queryKey: ["books"],
    queryFn: () => getBooks(),
  });

  useEffect(() => {
    if (!issueId || isLoading) {
      return;
    }
    const issue = issued.find(item => item.id === issueId);
    if (issue) {
      setSelectedIssue(issue);
      setViewMode("view");
    } else {
      toast({
        title: "Book Issue Not Found",
        description: "The requested issue record is unavailable.",
        variant: "destructive",
      });
      navigate("/vault-library-admin/book-issue", { replace: true });
    }
  }, [issueId, isLoading, issued, navigate, toast]);

  // Auto-fill member name and phone when member code changes
  useEffect(() => {
    if (form.memberCode.trim()) {
      const member = members.find(
        m => m.memberId.toLowerCase() === form.memberCode.trim().toLowerCase()
      );
      if (member) {
        setForm(prev => ({
          ...prev,
          memberName: member.name,
          memberPhone: member.phone,
        }));
      } else {
        setForm(prev => ({
          ...prev,
          memberName: "",
          memberPhone: "",
        }));
      }
    } else {
      setForm(prev => ({
        ...prev,
        memberName: "",
        memberPhone: "",
      }));
    }
  }, [form.memberCode, members]);

  // Auto-generate issue number for new entries
  useEffect(() => {
    if (viewMode === "add") {
      const nextNum = issued.length + 1;
      setForm(prev => ({
        ...prev,
        issueNo: `ISS-${String(nextNum).padStart(3, "0")}`,
      }));
    }
  }, [viewMode, issued.length]);

  // Mutation for issuing book
  const { mutate: doIssue, isPending } = useMutation({
    mutationFn: issueBook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["book-issues"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Book Issued Successfully",
        description: `Book "${form.bookName}" issued to ${form.memberName} (${form.memberCode}).`,
      });
      setForm(initialForm);
      setViewMode("list");
    },
    onError: error => {
      toast({
        title: "Unable to issue book",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateBookIssue(id, data),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["book-issues"] });
      toast({
        title: "Issue Updated",
        description: `Issue ${updated.issueNo} updated successfully.`,
      });
      setSelectedIssue(null);
      setForm(initialForm);
      setViewMode("list");
      navigate("/vault-library-admin/book-issue");
    },
    onError: error => {
      toast({
        title: "Unable to update issue",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBookIssue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["book-issues"] });
      toast({
        title: "Issue Deleted",
        description: "Book issue record removed.",
      });
      setDeletingId(null);
    },
    onError: error => {
      toast({
        title: "Unable to delete issue",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Filter logic
  const filtered = issued.filter(i => {
    const matchesSearch =
      i.memberName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.bookTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.bookNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.memberId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.issueNo.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = filterStatus === "all" || i.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const activeCount = issued.filter(i => i.status === "active").length;
  const overdueCount = issued.filter(i => i.status === "overdue").length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.memberCode || !form.bookName) {
      toast({
        title: "Validation Error",
        description: "Member Code and Book Name are required.",
        variant: "destructive",
      });
      return;
    }
    if (!form.memberName) {
      toast({
        title: "Invalid Member Code",
        description:
          "No member found with this code. Please check and try again.",
        variant: "destructive",
      });
      return;
    }
    // Find the book number from the book name
    const matchedBook = books.find(
      b => b.title.toLowerCase() === form.bookName.trim().toLowerCase()
    );
    if (!matchedBook) {
      toast({
        title: "Invalid Book",
        description: "Select an available book from the book list.",
        variant: "destructive",
      });
      return;
    }
    doIssue({
      issueNo: form.issueNo,
      memberCode: form.memberCode,
      memberName: form.memberName,
      memberMobile: form.memberPhone,
      bookName: matchedBook.title,
      bookId: /^\d+$/.test(matchedBook.id)
        ? Number(matchedBook.id)
        : matchedBook.id,
      issueDate: form.issueDate,
      dueDate: form.dueDate,
      remarks: form.remarks,
    });
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIssue) {
      return;
    }
    const matchedBook = books.find(
      b => b.title.toLowerCase() === form.bookName.trim().toLowerCase()
    );
    updateMutation.mutate({
      id: selectedIssue.id,
      data: {
        issueNo: form.issueNo,
        memberCode: form.memberCode,
        memberName: form.memberName,
        memberMobile: form.memberPhone,
        bookName: form.bookName,
        bookId: matchedBook?.id || selectedIssue.bookId,
        issueDate: form.issueDate,
        dueDate: form.dueDate,
        remarks: form.remarks,
      },
    });
  };

  const startView = (issue: BookIssueType) => {
    navigate(`/vault-library-admin/book-issue/${encodeURIComponent(issue.id)}`);
  };

  const startEdit = (issue: BookIssueType) => {
    setSelectedIssue(issue);
    setForm({
      issueNo: issue.issueNo,
      memberCode: issue.memberId,
      memberName: issue.memberName,
      memberPhone:
        issue.memberMobile ||
        members.find(m => m.memberId === issue.memberId)?.phone ||
        "",
      bookName: issue.bookTitle,
      remarks: issue.remarks || "",
      issueDate: issue.issueDate,
      dueDate: issue.dueDate,
    });
    setViewMode("edit");
  };

  // ─── FORM FIELDS RENDERER (Shared between Add/Edit/View) ───
  const renderFormFields = (readOnly: boolean) => {
    const data =
      readOnly && selectedIssue
        ? {
            issueNo: selectedIssue.issueNo,
            memberCode: selectedIssue.memberId,
            memberName: selectedIssue.memberName,
            memberPhone:
              selectedIssue.memberMobile ||
              members.find(m => m.memberId === selectedIssue.memberId)?.phone ||
              "—",
            bookName: selectedIssue.bookTitle,
            remarks: selectedIssue.remarks || "",
            issueDate: selectedIssue.issueDate,
            dueDate: selectedIssue.dueDate,
          }
        : form;

    return (
      <div className="space-y-6">
        {/* Row 1: Book Issue No & Member Code */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <FileText className="h-4 w-4 text-accent" />
              Book Issue No. <span className="text-destructive">*</span>
            </Label>
            {readOnly ? (
              <div className="rounded-xl border border-border/50 bg-muted/40 p-3 font-mono text-sm font-semibold text-primary">
                {data.issueNo}
              </div>
            ) : (
              <Input
                placeholder="e.g. ISS-007"
                className="input-field font-mono"
                value={data.issueNo}
                onChange={e => setForm({ ...form, issueNo: e.target.value })}
                readOnly
              />
            )}
            <p className="text-[11px] text-muted-foreground">
              Auto-generated issue number
            </p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <User className="h-4 w-4 text-accent" />
              Member Code <span className="text-destructive">*</span>
            </Label>
            {readOnly ? (
              <div className="rounded-xl border border-border/50 bg-muted/40 p-3 font-mono text-sm font-semibold">
                {data.memberCode}
              </div>
            ) : (
              <Select
                value={data.memberCode}
                onValueChange={val => setForm({ ...form, memberCode: val })}
              >
                <SelectTrigger className="input-field font-mono">
                  <SelectValue placeholder="Select Member Code" />
                </SelectTrigger>
                <SelectContent>
                  {members
                    .filter(m => m.status === "active")
                    .map(member => (
                      <SelectItem key={member.id} value={member.memberId}>
                        {member.memberId} — {member.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-[11px] text-muted-foreground">
              Select a member code to auto-fill name & mobile
            </p>
          </div>
        </div>

        {/* Row 2: Member Name (Auto-fill) & Member Mobile (Auto-fill) */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <User className="h-4 w-4 text-secondary" />
              Member Name
              <Badge
                variant="outline"
                className="border-accent/30 px-1.5 py-0 text-[10px] font-semibold text-accent"
              >
                Auto-fill / manual
              </Badge>
            </Label>
            {readOnly ? (
              <div className="rounded-xl border border-border/50 bg-muted/40 p-3 text-sm font-semibold text-foreground">
                {data.memberName || "—"}
              </div>
            ) : (
              <Input
                placeholder="Auto-filled from Member Code"
                className="input-field bg-muted/30"
                value={form.memberName}
                onChange={e => setForm({ ...form, memberName: e.target.value })}
              />
            )}
            {!readOnly && form.memberCode && !form.memberName && (
              <p className="flex items-center gap-1 text-[11px] text-destructive">
                <AlertTriangle className="h-3 w-3" /> No member found with code
                "{form.memberCode}"
              </p>
            )}
            {!readOnly && form.memberName && (
              <p className="flex items-center gap-1 text-[11px] text-success">
                ✓ Member found
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Phone className="h-4 w-4 text-secondary" />
              Member Mobile Number
              <Badge
                variant="outline"
                className="border-accent/30 px-1.5 py-0 text-[10px] font-semibold text-accent"
              >
                Auto-fill / manual
              </Badge>
            </Label>
            {readOnly ? (
              <div className="rounded-xl border border-border/50 bg-muted/40 p-3 font-mono text-sm text-foreground">
                {data.memberPhone || "—"}
              </div>
            ) : (
              <Input
                placeholder="Auto-filled from Member Code"
                className="input-field bg-muted/30 font-mono"
                value={form.memberPhone}
                onChange={e =>
                  setForm({ ...form, memberPhone: e.target.value })
                }
              />
            )}
          </div>
        </div>

        {/* Row 3: Book Name */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <BookOpen className="h-4 w-4 text-accent" />
            Book Name <span className="text-destructive">*</span>
          </Label>
          {readOnly ? (
            <div className="rounded-xl border border-border/50 bg-muted/40 p-3 text-sm font-semibold text-primary">
              {data.bookName}
            </div>
          ) : (
            <>
              <Select
                value={form.bookName}
                onValueChange={val => setForm({ ...form, bookName: val })}
              >
                <SelectTrigger className="input-field">
                  <SelectValue placeholder="Select Book" />
                </SelectTrigger>
                <SelectContent>
                  {books
                    .filter(b => b.status === "available")
                    .map(b => (
                      <SelectItem key={b.id} value={b.title}>
                        {b.bookNo} — {b.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Select a book from the available inventory
              </p>
            </>
          )}
        </div>

        {/* Row 4: Issue Date & Due Date */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-foreground">
              Issue Date
            </Label>
            {readOnly ? (
              <div className="rounded-xl border border-border/50 bg-muted/40 p-3 text-sm">
                {data.issueDate}
              </div>
            ) : (
              <Input
                type="date"
                className="input-field"
                value={form.issueDate}
                onChange={e => setForm({ ...form, issueDate: e.target.value })}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold text-foreground">
              Due Date
            </Label>
            {readOnly ? (
              <div className="rounded-xl border border-border/50 bg-muted/40 p-3 text-sm font-semibold">
                {data.dueDate}
              </div>
            ) : (
              <Input
                type="date"
                className="input-field"
                value={form.dueDate}
                onChange={e => setForm({ ...form, dueDate: e.target.value })}
              />
            )}
          </div>
        </div>

        {/* Row 5: Remarks */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Remarks
          </Label>
          {readOnly ? (
            <div className="min-h-[60px] rounded-xl border border-border/50 bg-muted/40 p-3 text-sm text-muted-foreground">
              {data.remarks || "No remarks"}
            </div>
          ) : (
            <Textarea
              placeholder="Add any additional notes or remarks..."
              className="input-field min-h-[80px] resize-none"
              value={form.remarks}
              onChange={e => setForm({ ...form, remarks: e.target.value })}
            />
          )}
        </div>
      </div>
    );
  };

  const handleExportCSV = () => {
    let csvContent =
      "Issue No,Member ID,Member Name,Book No,Book Title,Issue Date,Due Date,Status\n";
    filtered.forEach(i => {
      csvContent += `"${i.issueNo}","${i.memberId}","${i.memberName}","${i.bookNo}","${i.bookTitle}","${i.issueDate}","${i.dueDate}","${i.status}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "book_issues_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filtered);

  return (
    <DashboardLayout
      title="Book Issue Management"
      subtitle="Issue books to members, track active issues, and manage due dates"
    >
      {/* ━━━ SCREEN 1: ISSUE LIST ━━━ */}
      {viewMode === "list" && (
        <div className="animate-fade-in space-y-6">
          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                label: "Currently Issued",
                value: issued.length,
                color: "text-primary",
              },
              { label: "Active", value: activeCount, color: "text-success" },
              {
                label: "Overdue",
                value: overdueCount,
                color: "text-destructive",
              },
            ].map(s => (
              <div
                key={s.label}
                className="rounded-xl border border-border/40 bg-card p-4 shadow-sm"
              >
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className={`mt-1 text-2xl font-bold ${s.color}`}>
                  {isLoading ? "—" : s.value}
                </p>
              </div>
            ))}
          </div>

          {/* List Section */}
          <div className="form-section">
            <div className="mb-6 flex flex-col items-start justify-between gap-4 border-b pb-4 sm:flex-row sm:items-center">
              <div>
                <h2 className="text-lg font-bold text-foreground">
                  Issued Books
                </h2>
                <p className="text-xs text-muted-foreground">
                  Showing {filtered.length} of {issued.length} active
                  transactions
                </p>
              </div>

              <div className="flex w-full items-center gap-2 sm:w-auto">
                <Button
                  onClick={() => {
                    setForm(initialForm);
                    setViewMode("add");
                  }}
                  className="btn-primary flex-1 sm:flex-initial"
                >
                  <Plus className="mr-2 h-4 w-4" /> Issue New Book
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={handleExportCSV}
                >
                  <FileDown className="mr-2 h-4 w-4" /> Export CSV
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 sm:grid-cols-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search member, book title, issue no..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="input-field pl-9 text-xs"
                />
              </div>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Status Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="overdue">Overdue Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Issue No</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Book</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : isError ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-12 text-center text-destructive"
                      >
                        {issuesError instanceof Error
                          ? issuesError.message
                          : "Unable to load book issues."}
                      </TableCell>
                    </TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No matching records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map(issue => (
                      <TableRow key={issue.id} className="table-row-hover">
                        <TableCell className="font-mono text-xs font-semibold">
                          {issue.issueNo}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {issue.memberName}
                            </p>
                            <p className="font-mono text-xs text-muted-foreground">
                              {issue.memberId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-semibold text-primary">
                              {issue.bookTitle}
                            </p>
                            <p className="font-mono text-xs text-muted-foreground">
                              {issue.bookNo}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {issue.issueDate}
                        </TableCell>
                        <TableCell className="text-xs font-semibold text-muted-foreground">
                          {issue.dueDate}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              issue.status === "overdue"
                                ? "badge-destructive"
                                : "badge-success"
                            }
                          >
                            {issue.status === "overdue" ? "Overdue" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 rounded-lg border-primary/30 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground"
                              onClick={() => startView(issue)}
                              title="View"
                              aria-label={`View issue ${issue.issueNo}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg text-accent hover:bg-accent/10 hover:text-accent"
                              onClick={() => startEdit(issue)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg text-destructive hover:bg-destructive/10"
                              onClick={() => setDeletingId(issue.id)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* ━━━ SCREEN 2: ADD NEW ISSUE (Full Form Screen) ━━━ */}
      {viewMode === "add" && (
        <div className="animate-fade-in space-y-6">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              {/* Header */}
              <div className="mb-8 flex flex-wrap items-center justify-between gap-4 gap-y-3 border-b pb-4">
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="rounded-xl"
                    onClick={() => {
                      setForm(initialForm);
                      setViewMode("list");
                    }}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div>
                    <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
                      <BookOpen className="h-5 w-5 text-accent" />
                      Issue New Book
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Fill in the details to issue a book to a member
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              {renderFormFields(false)}

              {/* Action Buttons */}
              <div className="mt-8 flex items-center justify-end gap-3 border-t pt-6">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => {
                    setForm(initialForm);
                    setViewMode("list");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={isPending || !form.memberCode || !form.bookName}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {isPending ? "Issuing…" : "Issue Book"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ━━━ SCREEN 3: VIEW ISSUE DETAILS (Read-Only) ━━━ */}
      {viewMode === "view" && selectedIssue && (
        <div className="animate-fade-in space-y-6">
          <div className="form-section">
            {/* Header */}
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4 gap-y-3 border-b pb-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-xl"
                  onClick={() => {
                    setSelectedIssue(null);
                    setViewMode("list");
                    navigate("/vault-library-admin/book-issue");
                  }}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
                    <Eye className="h-5 w-5 text-secondary" />
                    Issue Details — {selectedIssue.issueNo}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    View issued book transaction details
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={
                    selectedIssue.status === "overdue"
                      ? "badge-destructive"
                      : selectedIssue.status === "returned"
                        ? "badge-info"
                        : "badge-success"
                  }
                >
                  {selectedIssue.status.charAt(0).toUpperCase() +
                    selectedIssue.status.slice(1)}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={() => startEdit(selectedIssue)}
                >
                  <Edit className="mr-2 h-4 w-4" /> Edit
                </Button>
              </div>
            </div>

            {/* Read-Only Fields */}
            {renderFormFields(true)}

            {/* Back Button */}
            <div className="mt-8 flex items-center justify-end gap-3 border-t pt-6">
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => {
                  setSelectedIssue(null);
                  setViewMode("list");
                  navigate("/vault-library-admin/book-issue");
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to List
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ━━━ SCREEN 4: EDIT ISSUE (Form Screen) ━━━ */}
      {viewMode === "edit" && selectedIssue && (
        <div className="animate-fade-in space-y-6">
          <form onSubmit={handleUpdate}>
            <div className="form-section">
              {/* Header */}
              <div className="mb-8 flex flex-wrap items-center justify-between gap-4 gap-y-3 border-b pb-4">
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="rounded-xl"
                    onClick={() => {
                      setSelectedIssue(null);
                      setForm(initialForm);
                      setViewMode("list");
                      navigate("/vault-library-admin/book-issue");
                    }}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div>
                    <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
                      <Edit className="h-5 w-5 text-accent" />
                      Edit Issue — {selectedIssue.issueNo}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Modify the book issue record
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              {renderFormFields(false)}

              {/* Action Buttons */}
              <div className="mt-8 flex items-center justify-end gap-3 border-t pt-6">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => {
                    setSelectedIssue(null);
                    setForm(initialForm);
                    setViewMode("list");
                    navigate("/vault-library-admin/book-issue");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={updateMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" /> Update Issue
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}

      <Dialog
        open={Boolean(deletingId)}
        onOpenChange={open => !open && setDeletingId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" /> Delete Book Issue?
            </DialogTitle>
            <DialogDescription>
              This will permanently remove the selected issue record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingId && deleteMutation.mutate(deletingId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default BookIssuePage;
